import React, { useCallback, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import cn from 'classnames'

import styles from './styles.module.scss'

const MAX_HOUR = 24
const MAX_MINUTE = 60
const MAX_END_HOUR = 23
const MAX_END_MINUTE = 45

const onlyDigits = (value) => value.replace(/\D/g, '').slice(0, 2)

const clamp = (value, max) => {
  if (value === '') return ''

  const num = Number(value)
  if (Number.isNaN(num)) return ''

  if (num < 0) return '00'
  if (num > max) return String(max).padStart(2, '0')

  return String(num).padStart(2, '0')
}

const roundMinutesToQuarter = (value) => {
  if (value === '') return ''

  const raw = Number(value)
  if (Number.isNaN(raw)) return ''

  const bounded = Math.max(0, Math.min(MAX_MINUTE, raw))
  const rounded = Math.round(bounded / 15) * 15

  return String(Math.min(MAX_MINUTE, rounded)).padStart(2, '0')
}

const parseTime = (time = '00:00') => {
  const [hour, minute] = String(time).split(':')
  return {
    hour: clamp(onlyDigits(hour || ''), MAX_HOUR) || '00',
    minute: roundMinutesToQuarter(clamp(onlyDigits(minute || ''), MAX_MINUTE) || '00') || '00',
  }
}

const clampEndTime = (hourValue, minuteValue) => {
  const hour = clamp(hourValue, MAX_HOUR) || '00'
  const minute = roundMinutesToQuarter(clamp(minuteValue, MAX_MINUTE) || '00') || '00'

  const hourNumber = Number(hour)
  const minuteNumber = Number(minute)

  if (hourNumber > MAX_END_HOUR) {
    return { hour: '23', minute: '45' }
  }

  if (hourNumber === MAX_END_HOUR && minuteNumber > MAX_END_MINUTE) {
    return { hour: '23', minute: '45' }
  }

  return { hour, minute }
}

const EditTimeForm = forwardRef(({ value = { start: '00:00', end: '00:00' }, onChange, className }, ref) => {
  const parsedStart = parseTime(value.start)
  const start = clampEndTime(parsedStart.hour, parsedStart.minute)
  const parsedEnd = parseTime(value.end)
  const end = clampEndTime(parsedEnd.hour, parsedEnd.minute)

  const [fields, setFields] = useState({
    startHour: start.hour,
    startMinute: start.minute,
    endHour: end.hour,
    endMinute: end.minute,
  })

  const refs = useRef([])

  useImperativeHandle(ref, () => ({
    setForm: (data) => {
      const parsedStart = parseTime(data.start)
      const start = clampEndTime(parsedStart.hour, parsedStart.minute)
      const parsedEnd = parseTime(data.end)
      const end = clampEndTime(parsedEnd.hour, parsedEnd.minute)

      setFields({
        startHour: start.hour,
        startMinute: start.minute,
        endHour: end.hour,
        endMinute: end.minute,
      })

      setTimeout(() => {
        refs.current[0].focus()
        refs.current[0].select()
      }, 250)
    },
    getForm: () => ({
      start: `${clampEndTime(fields.startHour, fields.startMinute).hour}:${clampEndTime(fields.startHour, fields.startMinute).minute}`,
      end: `${clampEndTime(fields.endHour, fields.endMinute).hour}:${clampEndTime(fields.endHour, fields.endMinute).minute}`,
    })
  }), [fields])

  const emitChange = useCallback((nextState) => {
    if (!onChange) return

    const safeStart = clampEndTime(nextState.startHour, nextState.startMinute)
    const safeEnd = clampEndTime(nextState.endHour, nextState.endMinute)

    onChange({
      start: `${safeStart.hour}:${safeStart.minute}`,
      end: `${safeEnd.hour}:${safeEnd.minute}`,
    })
  }, [onChange])

  const focusNext = (index) => {
    const next = refs.current[index + 1]
    if (next) {
      next.focus()
      next.select()
    }
  }

  const handleChange = (key, max, shouldRoundMinute, index) => (event) => {
    const sanitized = onlyDigits(event.target.value)
    const nextValue = sanitized

    setFields((prevState) => {
      const nextState = {
        ...prevState,
        [key]: nextValue,
      }
      emitChange(nextState)
      return nextState
    })

    if (sanitized.length === 2) {
      const normalized = shouldRoundMinute
        ? roundMinutesToQuarter(clamp(sanitized, max))
        : clamp(sanitized, max)

      setFields((prevState) => {
        const withNormalizedField = {
          ...prevState,
          [key]: normalized,
        }

        const startTime = clampEndTime(withNormalizedField.startHour, withNormalizedField.startMinute)
        const endTime = clampEndTime(withNormalizedField.endHour, withNormalizedField.endMinute)

        const nextState = {
          ...withNormalizedField,
          startHour: startTime.hour,
          startMinute: startTime.minute,
          endHour: endTime.hour,
          endMinute: endTime.minute,
        }

        emitChange(nextState)
        return nextState
      })

      focusNext(index)
    }
  }

  const handleBlur = (key, max, shouldRoundMinute) => () => {
    setFields((prevState) => {
      const raw = prevState[key]
      if (raw === '') {
        emitChange(prevState)
        return prevState
      }

      const normalized = shouldRoundMinute
        ? roundMinutesToQuarter(clamp(raw, max))
        : clamp(raw, max)

      const nextState = {
        ...prevState,
        [key]: normalized,
      }

      const startTime = clampEndTime(nextState.startHour, nextState.startMinute)
      const endTime = clampEndTime(nextState.endHour, nextState.endMinute)

      const finalState = {
        ...nextState,
        startHour: startTime.hour,
        startMinute: startTime.minute,
        endHour: endTime.hour,
        endMinute: endTime.minute,
      }

      emitChange(finalState)
      return finalState
    })
  }

  return (
    <div className={cn(styles.form, className)}>
      <div className={styles.timeGroup}>
        <input
          ref={(node) => { refs.current[0] = node }}
          onClick={e => e.stopPropagation()}
          className={styles.input}
          inputMode='numeric'
          pattern='[0-9]*'
          value={fields.startHour}
          onChange={handleChange('startHour', MAX_END_HOUR, false, 0)}
          onBlur={handleBlur('startHour', MAX_END_HOUR, false)}
          placeholder='HH'
          aria-label='Start hour'
        />
        <span className={styles.separator}>:</span>
        <input
          ref={(node) => { refs.current[1] = node }}
          onClick={e => e.stopPropagation()}
          className={styles.input}
          inputMode='numeric'
          pattern='[0-9]*'
          value={fields.startMinute}
          onChange={handleChange('startMinute', MAX_MINUTE, true, 1)}
          onBlur={handleBlur('startMinute', MAX_MINUTE, true)}
          placeholder='MM'
          aria-label='Start minute'
        />
      </div>

      <span className={styles.rangeSeparator}>-</span>

      <div className={styles.timeGroup}>
        <input
          ref={(node) => { refs.current[2] = node }}
          onClick={e => e.stopPropagation()}
          className={styles.input}
          inputMode='numeric'
          pattern='[0-9]*'
          value={fields.endHour}
          onChange={handleChange('endHour', MAX_END_HOUR, false, 2)}
          onBlur={handleBlur('endHour', MAX_END_HOUR, false)}
          placeholder='HH'
          aria-label='End hour'
        />
        <span className={styles.separator}>:</span>
        <input
          ref={(node) => { refs.current[3] = node }}
          onClick={e => e.stopPropagation()}
          className={styles.input}
          inputMode='numeric'
          pattern='[0-9]*'
          value={fields.endMinute}
          onChange={handleChange('endMinute', MAX_MINUTE, true, 3)}
          onBlur={handleBlur('endMinute', MAX_MINUTE, true)}
          placeholder='MM'
          aria-label='End minute'
        />
      </div>
    </div>
  )
})

export default EditTimeForm
